// logDocumentPayment.js
//
// Log an offline payment against a DOCUMENT_PAYMENT installment.
// Partial payments spawn a remainder scheduled row (Houzz model — no partial status).

import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { updateNode } from 'services/nodes/NodeWriteService';
import { enqueueInvoiceQboSync } from 'services/quickbooks/QuickBooksService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { createDocumentPayment } from 'nodetypes/documents/payments/createDocumentPayment';
import {
  DOCUMENT_PAYMENT_TYPE_KEY,
} from 'nodetypes/documents/payments/documentPaymentKeys';
import {
  isDocumentPaymentOpenStatus,
  readDocumentPaymentField,
  toDocumentPaymentMoneyNumber,
} from 'nodetypes/documents/payments/documentPaymentsModel';

function normalizeString(value) {
  return String(value || '').trim();
}

function mergePaymentFields(payment, fields) {
  let node = payment;
  const patch = {};
  Object.entries(fields).forEach(([fieldName, value]) => {
    if (value === undefined) return;
    const fieldPatch = buildNodeFieldPatch(
      { ...node, ...patch },
      {
        name: fieldName,
        source: 'document_payment',
        type: fieldName === 'amount' || fieldName === 'feeCents' ? 'money' : 'string',
      },
      DOCUMENT_PAYMENT_TYPE_KEY,
      value,
    );
    if (!fieldPatch) return;
    Object.entries(fieldPatch).forEach(([sectionKey, sectionValue]) => {
      patch[sectionKey] = {
        ...(patch[sectionKey] || {}),
        ...sectionValue,
      };
      node = {
        ...node,
        [sectionKey]: {
          ...(node[sectionKey] || {}),
          ...sectionValue,
        },
      };
    });
  });
  return patch;
}

/**
 * @param {object} options
 * @param {object} options.payment
 * @param {object} options.parentNode
 * @param {object} options.user
 * @param {string|null} [options.baseKey]
 * @param {object|null} [options.paymentType]
 * @param {object|null} [options.parentType]
 * @param {number} options.amountCents
 * @param {string} options.method
 * @param {unknown} [options.paidDate]
 * @param {string} [options.reference]
 * @param {number} [options.existingCount]
 * @returns {Promise<{ payment: object, remainder: object|null }>}
 */
export async function logDocumentPayment({
  payment,
  parentNode,
  user,
  baseKey = null,
  paymentType = null,
  parentType = null,
  amountCents,
  method,
  paidDate = null,
  reference = '',
  existingCount = 0,
}) {
  if (!payment?.key || !parentNode?.key || !user?.key) {
    throw new Error('Payment, parent document, and user are required to log a payment.');
  }

  const status = readDocumentPaymentField(payment, 'status');
  if (!isDocumentPaymentOpenStatus(status) && normalizeString(status).toLowerCase() !== 'paid') {
    throw new Error('Only scheduled or due installments can be logged.');
  }

  const scheduledAmount = toDocumentPaymentMoneyNumber(
    readDocumentPaymentField(payment, 'amount'),
  ) || 0;
  const loggedAmount = Math.round(Number(amountCents));
  if (!Number.isFinite(loggedAmount) || loggedAmount <= 0) {
    throw new Error('Enter a payment amount greater than zero.');
  }

  const resolvedMethod = normalizeString(method);
  if (!resolvedMethod) {
    throw new Error('Choose a payment method.');
  }

  const resolvedPaidDate = paidDate || new Date().toISOString();
  const remainderCents = scheduledAmount > 0 ? scheduledAmount - loggedAmount : 0;

  const paidFields = {
    status: 'paid',
    amount: loggedAmount,
    method: resolvedMethod,
    paidDate: resolvedPaidDate,
    reference: normalizeString(reference),
    source: 'manual',
    amountBasis: 'fixed',
  };

  const patch = mergePaymentFields(payment, paidFields);
  await updateNode(payment.key, patch);

  let remainder = null;
  if (remainderCents > 0) {
    const paymentTerms = readDocumentPaymentField(payment, 'paymentTerms');
    const dueDate = readDocumentPaymentField(payment, 'dueDate');
    const sortOrder = Number(readDocumentPaymentField(payment, 'sortOrder'));
    remainder = await createDocumentPayment({
      parentNode,
      user,
      baseKey,
      paymentType: paymentType || getNodeType(DOCUMENT_PAYMENT_TYPE_KEY),
      parentType,
      existingCount,
      defaultAmountCents: remainderCents,
      amountBasis: 'fixed',
      title: `${normalizeString(payment.title) || 'Payment'} (remainder)`,
      sortOrder: Number.isFinite(sortOrder) ? sortOrder + 1 : null,
    });

    // Copy terms/due onto remainder if create didn't (parent defaults may differ).
    if (remainder?.key) {
      const remainderPatch = mergePaymentFields(remainder, {
        paymentTerms: paymentTerms || undefined,
        dueDate: dueDate || undefined,
      });
      if (Object.keys(remainderPatch).length) {
        await updateNode(remainder.key, remainderPatch);
      }
    }
  }

  if (String(parentNode?.type || '').trim().toUpperCase() === 'INVOICE') {
    enqueueInvoiceQboSync({
      baseKey,
      invoiceNode: parentNode,
      reason: 'payment',
    });
  }

  return { payment: { ...payment, ...patch }, remainder };
}
